import React, { useEffect } from 'react'

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ')
}

function splitTokens(input?: string) {
  return (input || '')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean)
}

function colorToClass(color?: string) {
  const tokens = splitTokens(color)
  const first = tokens[0]

  switch (first) {
    case 'blue-gray':
      return 'text-blue-gray-700'
    case 'red':
      return 'text-red-600'
    case 'gray':
      return 'text-gray-700'
    case 'white':
      return 'text-white'
    case 'black':
      return 'text-black'
    default:
      return ''
  }
}

function extraClassTokens(color?: string, variant?: string) {
  const colorTokens = splitTokens(color).slice(1)
  const variantTokens = splitTokens(variant).slice(1)
  return cx(...[...colorTokens, ...variantTokens].map((t) => t))
}

export function Typography({
  variant,
  color,
  className,
  children,
  ...rest
}: {
  variant?: string
  color?: string
  className?: string
  children?: React.ReactNode
  [key: string]: any
}) {
  const tokens = splitTokens(variant)
  const variantToken = tokens[0]

  const extraTokensClass = extraClassTokens(color, variant)
  const computedColorClass = colorToClass(color)

  const commonClassName = cx(computedColorClass, extraTokensClass, className)

  if (!variantToken) {
    return (
      <span className={commonClassName} {...rest}>
        {children}
      </span>
    )
  }

  switch (variantToken) {
    case 'paragraph':
      return (
        <p className={commonClassName} {...rest}>
          {children}
        </p>
      )
    case 'small':
      return (
        <p className={commonClassName} {...rest}>
          {children}
        </p>
      )
    case 'h1':
      return (
        <h1 className={commonClassName} {...rest}>
          {children}
        </h1>
      )
    case 'h2':
      return (
        <h2 className={commonClassName} {...rest}>
          {children}
        </h2>
      )
    case 'h3':
      return (
        <h3 className={commonClassName} {...rest}>
          {children}
        </h3>
      )
    case 'h4':
      return (
        <h4 className={commonClassName} {...rest}>
          {children}
        </h4>
      )
    case 'h5':
      return (
        <h5 className={commonClassName} {...rest}>
          {children}
        </h5>
      )
    case 'h6':
      return (
        <h6 className={commonClassName} {...rest}>
          {children}
        </h6>
      )
    default:
      return (
        <p className={commonClassName} {...rest}>
          {children}
        </p>
      )
  }
}

export function Button({
  className,
  disabled,
  fullWidth,
  type,
  onClick,
  children,
  size,
}: {
  className?: string
  disabled?: boolean
  fullWidth?: boolean
  type?: 'button' | 'submit' | 'reset'
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  children?: React.ReactNode
  /** Mirrors Material Tailwind-ish defaults when not overridden in `className`. */
  size?: 'sm' | 'md' | 'lg'
  [key: string]: any
}) {
  const sizePadding =
    size === 'sm'
      ? 'py-2 px-4 text-sm'
      : size === 'lg'
        ? 'py-3.5 px-7 text-sm'
        : 'py-3 px-6 text-sm'

  return (
    <button
      type={type || 'button'}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        'inline-flex items-center justify-center rounded-none transition-all disabled:opacity-50 disabled:pointer-events-none',
        sizePadding,
        fullWidth ? 'w-full' : '',
        className
      )}
    >
      {children}
    </button>
  )
}

function FormFieldWrapper({
  label,
  labelProps,
  children,
}: {
  label?: string
  labelProps?: { className?: string }
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      {label ? <label className={labelProps?.className || ''}>{label}</label> : null}
      {children}
    </div>
  )
}

export function Input({
  label,
  labelProps,
  className,
  error,
  disabled,
  onChange,
  type,
  name,
  id,
  value,
  placeholder,
}: {
  label?: string
  labelProps?: { className?: string }
  className?: string
  error?: boolean
  disabled?: boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  type?: string
  name?: string
  id?: string
  value?: any
  placeholder?: string
  [key: string]: any
}) {
  return (
    <FormFieldWrapper label={label} labelProps={labelProps}>
      <input
        type={type || 'text'}
        name={name}
        id={id}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={onChange}
        aria-invalid={error ? true : undefined}
        className={cx(
          'block w-full rounded-none border border-[#F4F0EE] bg-[#E2DDDB] px-3 py-2 text-black focus:outline-none',
          className
        )}
      />
    </FormFieldWrapper>
  )
}

export function Textarea({
  label,
  labelProps,
  className,
  error,
  disabled,
  onChange,
  name,
  id,
  value,
  placeholder,
  rows,
}: {
  label?: string
  labelProps?: { className?: string }
  className?: string
  error?: boolean
  disabled?: boolean
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>
  name?: string
  id?: string
  value?: any
  placeholder?: string
  rows?: number
  [key: string]: any
}) {
  return (
    <FormFieldWrapper label={label} labelProps={labelProps}>
      <textarea
        name={name}
        id={id}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={onChange}
        aria-invalid={error ? true : undefined}
        rows={rows}
        className={cx(
          'block w-full min-h-[120px] resize-y rounded-none border border-[#F4F0EE] bg-[#E2DDDB] px-3 py-2 text-black focus:outline-none',
          className
        )}
      />
    </FormFieldWrapper>
  )
}

export function Alert({
  color,
  className,
  children,
}: {
  color?: string
  className?: string
  children?: React.ReactNode
  [key: string]: any
}) {
  const textClass = color === 'red' ? 'text-white' : colorToClass(color)
  const bgClass =
    color === 'red'
      ? 'bg-red-600'
      : color === 'gray'
        ? 'bg-gray-700'
        : color === 'white'
          ? 'bg-white text-black'
          : 'bg-red-600'

  return <div role="alert" className={cx('p-4 text-sm', bgClass, textClass, className)}>{children}</div>
}

export function Select({
  label,
  labelProps,
  className,
  error,
  disabled,
  onChange,
  value,
  children,
}: {
  label?: string
  labelProps?: { className?: string }
  className?: string
  error?: boolean
  disabled?: boolean
  onChange?: (value: string) => void
  value?: any
  children?: React.ReactNode
  [key: string]: any
}) {
  return (
    <FormFieldWrapper label={label} labelProps={labelProps}>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        aria-invalid={error ? true : undefined}
        className={cx(
          'block w-full rounded-none border border-[#F4F0EE] bg-[#E2DDDB] px-3 py-2 text-black focus:outline-none',
          className
        )}
      >
        {children}
      </select>
    </FormFieldWrapper>
  )
}

export function Option({ value, children }: { value: string; children?: React.ReactNode }) {
  return <option value={value}>{children}</option>
}

export function Dialog({
  open,
  handler,
  children,
}: {
  open: boolean
  handler?: () => void
  children?: React.ReactNode
  [key: string]: any
}) {
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handler?.()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, handler])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={() => handler?.()}
      role="dialog"
    >
      <div onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  )
}

export function Card({ className, children }: { className?: string; children?: React.ReactNode }) {
  return <div className={className}>{children}</div>
}

export function CardBody({ className, children }: { className?: string; children?: React.ReactNode }) {
  return <div className={className}>{children}</div>
}

export function CardFooter({ className, children }: { className?: string; children?: React.ReactNode }) {
  return <div className={className}>{children}</div>
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cx('animate-spin text-current', className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-label="Loading"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
      />
    </svg>
  )
}

export function Checkbox({
  checked,
  readOnly,
  className,
}: {
  checked: boolean
  readOnly?: boolean
  className?: string
}) {
  return <input type="checkbox" checked={checked} readOnly={readOnly} className={className} />
}

